// @givmo/board — the two facts a member dialog records, shared by "Add a board member", "Edit this
// seat" and "Record someone who served before" (CBG-YR3-ROLES layer 1).
//
//   * DIRECTOR is Yes / No, never a checkbox: "No" is the choice that changes votes, quorum and
//     written consents, so it is made deliberately, with the one line that says what directors do.
//   * TITLES are the board's own (`GET …/officer-titles`), several allowed, director or not. The
//     catalog is read, never written: a title nobody holds yet carries no id, and the write names it
//     by its key. While the catalog is loading or couldn't be read, the field says so and the dialog
//     sends no `titles` at all — on an edit that leaves the person's titles exactly as they are.
//   * TITLE DATES (layer 2, `l2-member-edit-dates`): where a dialog edits them, each ticked title
//     takes "Held from" / "Held to" — the board's OWN dates for the title — as a dependent row marked
//     by a leading rule, and the list goes to one column so each row sits under its title.

import { useQuery } from '@tanstack/react-query';
import { Checkbox, RadioTile, RadioTileGroup, TextField } from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { useBoardOperations } from '../../board-runtime';
import { extractOfficerTitles, type CatalogTitle, type TitleDates } from './officer-roles';
import { officerRolesL2Words as l2 } from './officer-roles-l2-words';
import { officerRolesWords as w } from './officer-roles-words';

/** The board's titles for a dialog: the catalog's active rows in order, and where the read stands. */
export function useOfficerTitleCatalog(orgRef: string): {
  state: TitlesFieldState;
  catalog: CatalogTitle[];
} {
  const operations = useBoardOperations();
  const catalog = useQuery({
    ...operations.officerListBoardOfficerTitlesOptions({ path: { org_ref: orgRef } }),
    select: extractOfficerTitles,
  });
  return {
    state: catalog.isSuccess ? 'ready' : catalog.isError ? 'unavailable' : 'loading',
    catalog: catalog.data ?? [],
  };
}

const GROUP_LABEL = 'font-sans text-s13 font-w600 tracking-givmo text-text-strong';

export interface DirectorFieldProps {
  value: boolean;
  onChange: (isDirector: boolean) => void;
  disabled?: boolean;
  /** `roster-seat` or `roster-historical`: the dialog's own testid prefix. */
  testIdPrefix: string;
}

export function DirectorField({ value, onChange, disabled, testIdPrefix }: DirectorFieldProps) {
  return (
    <div className="flex flex-col gap-8" data-testid={`${testIdPrefix}-director`}>
      <span className={GROUP_LABEL}>{w.director}</span>
      <RadioTileGroup
        aria-label={w.director}
        value={value ? 'yes' : 'no'}
        onValueChange={(next) => onChange(next !== 'no')}
        disabled={disabled}
        className="flex-row gap-24"
      >
        <RadioTile value="yes" label={w.yes} testId={`${testIdPrefix}-director-yes`} />
        <RadioTile value="no" label={w.no} testId={`${testIdPrefix}-director-no`} />
      </RadioTileGroup>
      <FieldNote>{w.directorMeans}</FieldNote>
    </div>
  );
}

export type TitlesFieldState = 'loading' | 'unavailable' | 'ready';

export interface TitlesFieldProps {
  state: TitlesFieldState;
  choices: readonly CatalogTitle[];
  selected: readonly string[];
  onChange: (keys: string[]) => void;
  disabled?: boolean;
  /** The line under the boxes, when the dialog has one (the former-member dialog's). */
  note?: string;
  /** The line when the titles can't be read: an edit keeps the person's titles, an add records
   *  none (the default is the edit's). */
  unavailableLine?: string;
  /** Layer 2: each ticked title's own dates, by choice key — given, the field edits them. */
  dates?: Readonly<Record<string, TitleDates>>;
  onDatesChange?: (key: string, dates: TitleDates) => void;
  testIdPrefix: string;
}

const NO_DATES: TitleDates = { from: '', to: '' };

export function TitlesField({
  state,
  choices,
  selected,
  onChange,
  disabled,
  note,
  unavailableLine = w.titlesUnavailable,
  dates,
  onDatesChange,
  testIdPrefix,
}: TitlesFieldProps) {
  const editsDates = dates !== undefined && onDatesChange !== undefined;
  return (
    <fieldset className="flex min-w-0 flex-col gap-10" data-testid={`${testIdPrefix}-titles`}>
      <legend className={`mb-8 ${GROUP_LABEL}`}>{w.titles}</legend>
      {state === 'ready' ? (
        <div className={editsDates ? 'flex flex-col gap-10' : 'grid grid-cols-2 gap-x-16 gap-y-10'}>
          {choices.map((choice) => {
            const checked = selected.includes(choice.key);
            const testKey = choice.default_key ?? choice.key;
            const held = dates?.[choice.key] ?? NO_DATES;
            return (
              <div key={choice.key} className="flex min-w-0 flex-col gap-8">
                <label className="inline-flex min-w-0 cursor-pointer items-center gap-10">
                  <Checkbox
                    appearance="saas"
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={(next) =>
                      onChange(
                        next
                          ? [...selected.filter((key) => key !== choice.key), choice.key]
                          : selected.filter((key) => key !== choice.key),
                      )
                    }
                    testId={`${testIdPrefix}-title-${testKey}`}
                  />
                  <span className="min-w-0 font-sans text-s14 font-w500 tracking-givmo text-text-strong wrap-anywhere">
                    {choice.label}
                  </span>
                </label>
                {editsDates && checked ? (
                  // A dependent row, marked by a leading rule (never a fill, never indent alone).
                  <div
                    className="ml-8 grid min-w-0 grid-cols-2 gap-12 border-l-2 border-border-strong pl-16"
                    data-testid={`${testIdPrefix}-title-dates-${testKey}`}
                  >
                    <TextField
                      label={l2.heldFrom}
                      type="date"
                      value={held.from}
                      disabled={disabled}
                      onChange={(event) =>
                        onDatesChange(choice.key, { ...held, from: event.target.value })
                      }
                      testId={`${testIdPrefix}-title-from-${testKey}`}
                    />
                    <TextField
                      label={l2.heldTo}
                      type="date"
                      value={held.to}
                      disabled={disabled}
                      onChange={(event) =>
                        onDatesChange(choice.key, { ...held, to: event.target.value })
                      }
                      testId={`${testIdPrefix}-title-to-${testKey}`}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
          data-testid={`${testIdPrefix}-titles-${state}`}
        >
          {state === 'loading' ? w.titlesLoading : unavailableLine}
        </p>
      )}
      {editsDates && state === 'ready' ? (
        <FieldNote testId={`${testIdPrefix}-title-dates-note`}>{l2.titleDatesNote}</FieldNote>
      ) : null}
      {note !== undefined && state === 'ready' ? <FieldNote>{note}</FieldNote> : null}
    </fieldset>
  );
}
